<script>
import { nextTick, ref } from '@vue/composition-api';
import { formatNumberLabel } from '@/features/charting-next/adapters/chartingAdapterUtils';
import ChartingFavoriteColorPicker from './ChartingFavoriteColorPicker.vue';

export default {
    name: 'ChartingLeftDrawerFavoritesSection',
    components: { ChartingFavoriteColorPicker },
    props: {
        favoriteFolders: { type: Array, default: () => [] },
        entryType: { type: String, default: 'finder' },
        entryValue: { type: String, default: '' },
        canUseFavorites: { type: Boolean, default: false },
        canManageFavoriteFolders: { type: Boolean, default: false }
    },
    setup(props, { emit }) {
        const root = ref(null);
        const optionsPopover = ref(null);
        const optionsFolder = ref(null);
        const isCreating = ref(false);
        const favoriteDraft = ref({ name: '', color: '#0ea5e9' });
        const editingFavoriteName = ref('');
        const editingFavoriteDraft = ref({ newName: '', newColor: '#0ea5e9' });
        const folderCountLabel = folder => Number.isSafeInteger(folder.tickerCount) && folder.tickerCount >= 0
            ? `${formatNumberLabel(folder.tickerCount, { maximumFractionDigits: 0 })} ${folder.tickerCount === 1 ? 'valor' : 'valores'}`
            : '';

        const focus = (selector) => nextTick(() => root.value?.querySelector(selector)?.focus());
        const closeOptions = (restoreFocus = false) => optionsPopover.value?.hide({ restoreFocus });
        const toggleOptions = (folder, event) => {
            if (optionsFolder.value?.name === folder.name && optionsPopover.value?.visible) {
                closeOptions(true);
                return;
            }
            optionsFolder.value = folder;
            optionsPopover.value?.show(event);
            nextTick(() => optionsPopover.value?.$el.querySelector('button')?.focus());
        };
        const handleOptionsKeydown = (event) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            event.stopPropagation();
            closeOptions(true);
        };
        const resetFavoriteEdition = () => {
            editingFavoriteName.value = '';
            editingFavoriteDraft.value = { newName: '', newColor: '#0ea5e9' };
        };
        const startFavoriteEdition = (folder) => {
            closeOptions();
            isCreating.value = false;
            editingFavoriteName.value = folder.name;
            editingFavoriteDraft.value = { newName: folder.label, newColor: folder.color || '#0ea5e9' };
            focus('#charting-favorite-edit-name');
        };
        const cancelFavoriteEdition = () => {
            const folderName = editingFavoriteName.value;
            resetFavoriteEdition();
            nextTick(() => [...(root.value?.querySelectorAll('.charting-favorite-more') || [])]
                .find(button => button.dataset.folder === folderName)?.focus());
        };
        const submitFavoriteEdition = () => {
            if (!editingFavoriteName.value || !editingFavoriteDraft.value.newName.trim()) return;
            emit('rename-favorite-folder', {
                name: editingFavoriteName.value,
                newName: editingFavoriteDraft.value.newName.trim(),
                newColor: editingFavoriteDraft.value.newColor
            });
            resetFavoriteEdition();
        };
        const removeFavoriteFolder = (folder) => {
            closeOptions(true);
            emit('remove-favorite-folder', folder.name);
        };
        const startFavoriteCreate = () => {
            closeOptions();
            resetFavoriteEdition();
            isCreating.value = true;
            focus('#charting-favorite-create-name');
        };
        const cancelFavoriteCreate = () => {
            isCreating.value = false;
            favoriteDraft.value = { name: '', color: '#0ea5e9' };
            focus('.charting-favorite-create-trigger');
        };
        const submitFavoriteCreate = () => {
            if (!favoriteDraft.value.name.trim()) return;
            emit('create-favorite-folder', { name: favoriteDraft.value.name.trim(), color: favoriteDraft.value.color });
            cancelFavoriteCreate();
        };
        return {
            root, optionsPopover, optionsFolder, isCreating, favoriteDraft, editingFavoriteName, editingFavoriteDraft,
            toggleOptions, handleOptionsKeydown, startFavoriteEdition,
            cancelFavoriteEdition, submitFavoriteEdition, removeFavoriteFolder, startFavoriteCreate,
            cancelFavoriteCreate, submitFavoriteCreate, folderCountLabel, emit
        };
    }
};
</script>

<template>
    <div ref="root" class="charting-favorites">
        <template v-if="canUseFavorites">
            <p v-if="!favoriteFolders.length" class="charting-favorite-hint">
                {{ canManageFavoriteFolders ? 'Crea una carpeta para organizar tus valores.' : 'No hay carpetas disponibles.' }}
            </p>

            <div class="charting-favorite-folders">
                <div v-for="folder in favoriteFolders" :key="folder.id" class="charting-favorite-row" :class="{ 'is-editing': editingFavoriteName === folder.name }">
                    <form v-if="editingFavoriteName === folder.name" class="charting-favorite-form" @submit.prevent="submitFavoriteEdition">
                        <div class="charting-favorite-form__fields">
                            <div>
                                <span class="charting-favorite-form__label">Color</span>
                                <ChartingFavoriteColorPicker v-model="editingFavoriteDraft.newColor" :label="'Color de ' + folder.label" />
                            </div>
                            <div class="charting-favorite-form__name">
                                <label for="charting-favorite-edit-name" class="charting-favorite-form__label">Nombre de la carpeta</label>
                                <InputText id="charting-favorite-edit-name" v-model="editingFavoriteDraft.newName" autocomplete="off" />
                            </div>
                        </div>
                        <div class="charting-favorite-form__actions">
                            <Button type="submit" label="Guardar cambios" size="small" :disabled="!editingFavoriteDraft.newName.trim()" />
                            <Button label="Cancelar" size="small" severity="secondary" text @click="cancelFavoriteEdition" />
                        </div>
                    </form>
                    <template v-else>
                        <button
                            type="button"
                            class="charting-favorite-open"
                            :class="{ 'is-active': entryType === 'favorites' && entryValue === folder.name }"
                            :aria-label="'Abrir carpeta ' + folder.label + (folderCountLabel(folder) ? ', ' + folderCountLabel(folder) : '')"
                            @click="emit('open-source', { type: 'favorites', value: folder.name })"
                        >
                            <span class="charting-favorite-folder-dot" :style="{ backgroundColor: folder.color }" aria-hidden="true"></span>
                            <span class="charting-favorite-folder-name">{{ folder.label }}</span>
                            <span v-if="folderCountLabel(folder)" class="charting-favorite-folder-count">{{ folderCountLabel(folder) }}</span>
                            <i class="pi pi-angle-right charting-favorite-open__arrow" aria-hidden="true"></i>
                        </button>
                        <button
                            v-if="canManageFavoriteFolders"
                            type="button"
                            class="charting-favorite-more"
                            :data-folder="folder.name"
                            :aria-label="'Opciones de ' + folder.label"
                            :title="'Opciones de ' + folder.label"
                            aria-haspopup="dialog"
                            :aria-expanded="String(Boolean(optionsFolder && optionsFolder.name === folder.name && optionsPopover && optionsPopover.visible))"
                            @click="toggleOptions(folder, $event)"
                        ><i class="pi pi-ellipsis-h" aria-hidden="true"></i></button>
                    </template>
                </div>
            </div>

            <div v-if="canManageFavoriteFolders" class="charting-favorite-create">
                <button v-if="!isCreating" type="button" class="charting-favorite-create-trigger" @click="startFavoriteCreate">
                    <i class="pi pi-plus" aria-hidden="true"></i> Nueva carpeta
                </button>
                <form v-else class="charting-favorite-form" @submit.prevent="submitFavoriteCreate">
                    <div class="charting-favorite-form__fields">
                        <div>
                            <span class="charting-favorite-form__label">Color</span>
                            <ChartingFavoriteColorPicker v-model="favoriteDraft.color" label="Color de la nueva carpeta" />
                        </div>
                        <div class="charting-favorite-form__name">
                            <label for="charting-favorite-create-name" class="charting-favorite-form__label">Nueva carpeta</label>
                            <InputText id="charting-favorite-create-name" v-model="favoriteDraft.name" placeholder="Nombre de la carpeta" autocomplete="off" />
                        </div>
                    </div>
                    <div class="charting-favorite-form__actions">
                        <Button type="submit" label="Crear carpeta" size="small" :disabled="!favoriteDraft.name.trim()" />
                        <Button label="Cancelar" size="small" severity="secondary" text @click="cancelFavoriteCreate" />
                    </div>
                </form>
            </div>

            <Popover ref="optionsPopover" class="charting-favorite-options" @keydown.native="handleOptionsKeydown">
                <template v-if="optionsFolder">
                    <button type="button" :aria-label="'Editar carpeta ' + optionsFolder.label" @click="startFavoriteEdition(optionsFolder)">
                        <i class="pi pi-pencil" aria-hidden="true"></i> Editar
                    </button>
                    <button type="button" class="charting-favorite-remove" :aria-label="'Eliminar carpeta ' + optionsFolder.label" @click="removeFavoriteFolder(optionsFolder)">
                        <i class="pi pi-trash" aria-hidden="true"></i> Eliminar
                    </button>
                </template>
            </Popover>
        </template>
        <p v-else class="charting-favorite-hint">Tu plan actual no permite gestionar favoritos.</p>
    </div>
</template>

<style scoped>
.charting-favorites { min-width: 0; }
.charting-favorite-hint { margin: 0.4rem 0 1rem; color: var(--p-text-muted-color); font-size: 0.82rem; line-height: 1.5; }
.charting-favorite-row { display: flex; align-items: center; gap: 0.5rem; min-height: 3.75rem; border-bottom: 1px solid var(--charting-b-border-color); padding: 0.3rem 0; }
.charting-favorite-row:last-child { border-bottom: 0; }
.charting-favorites button { font: inherit; cursor: pointer; }
.charting-favorites button:focus-visible { outline: 2px solid var(--brand-primary); outline-offset: 2px; }
.charting-favorite-open { display: flex; flex: 1 1 auto; align-items: center; gap: 0.6rem; min-width: 0; min-height: 2.75rem; padding: 0.45rem 0.3rem; border: 0; border-radius: 0.5rem; background: transparent; color: var(--p-text-color); text-align: left; }
.charting-favorite-folder-name { min-width: 0; overflow-wrap: anywhere; font-size: 0.9rem; }
.charting-favorite-folder-count { flex-shrink: 0; margin-left: auto; color: var(--p-text-muted-color); font-size: 0.78rem; font-variant-numeric: tabular-nums; white-space: nowrap; }
.charting-favorite-folder-dot { flex-shrink: 0; width: 0.6rem; height: 0.6rem; border-radius: 50%; box-shadow: inset 0 0 0 1px rgba(128, 128, 128, 0.25); }
.charting-favorite-open__arrow { flex-shrink: 0; color: var(--p-text-muted-color); font-size: 0.85rem; }
.charting-favorite-open:hover, .charting-favorite-open.is-active { background: var(--charting-b-hover-bg); }
.charting-favorite-more { flex-shrink: 0; width: 2.75rem; height: 2.75rem; border: 0; border-radius: 0.5rem; background: transparent; color: var(--p-text-muted-color); }
.charting-favorite-more:hover { background: var(--charting-b-panel-bg); color: var(--p-text-color); }
.charting-favorite-options { width: 11rem; padding: 0.35rem; border-radius: 0.75rem; }
.charting-favorite-options button { display: flex; align-items: center; gap: 0.65rem; width: 100%; min-height: 2.75rem; padding: 0.6rem 0.75rem; border: 0; border-radius: 0.5rem; background: transparent; color: var(--p-text-color); text-align: left; }
.charting-favorite-options button:hover { background: var(--charting-b-hover-bg); }
.charting-favorite-options .charting-favorite-remove { color: var(--p-red-600, #dc2626); }
.charting-favorite-create { margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid var(--charting-b-border-color); }
.charting-favorite-create-trigger { display: inline-flex; align-items: center; gap: 0.65rem; min-height: 2.75rem; border: 0; border-radius: 0.5rem; padding: 0.5rem 0.3rem; background: transparent; color: var(--p-text-color); }
.charting-favorite-create-trigger:hover { color: var(--brand-primary); }
.charting-favorite-form { width: 100%; padding: 0.5rem 0; }
.charting-favorite-form__fields { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: end; gap: 0.75rem; }
.charting-favorite-form__name { min-width: 0; }
.charting-favorite-form__label { display: block; margin-bottom: 0.45rem; color: var(--p-text-muted-color); font-size: 0.8rem; }
.charting-favorite-form ::v-deep .p-inputtext { min-width: 0; min-height: 2.875rem; }
.charting-favorite-form__actions { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.75rem; }
@media (max-width: 479px) {
    .charting-favorite-row { gap: 0.15rem; }
    .charting-favorite-more { width: 2.5rem; }
    .charting-favorite-open { gap: 0.4rem; }
}
</style>
